import {DraggableLocation, DroppableId} from 'react-beautiful-dnd';

import {TStateSetter} from 'shared/types/generics';

import {
    TReturningDraggableComponent,
} from './types';

/**
 * Moves an items inside of list
 */
const reorder = (list: TReturningDraggableComponent[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (
    source: TReturningDraggableComponent[],
    destination: TReturningDraggableComponent[],
    droppableSource: DraggableLocation,
    droppableDestination: DraggableLocation,
) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    const result: Record<DroppableId, TReturningDraggableComponent[]> = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destClone;

    return result;
};

/**
 * Moves an item from one list to new empty list.
 */
export const moveToNewColumn = (
    source: TReturningDraggableComponent[],
    droppableSource: DraggableLocation,
    droppableDestinationId: number,
) => {
    const sourceClone = Array.from(source);
    const [removed] = sourceClone.splice(droppableSource.index, 1);
    const result: Record<DroppableId, TReturningDraggableComponent[]> = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestinationId] = [removed];
    return result;
};

// Проверка попадания в swap-зону
export const isOverSwapArea = (x: number, y: number, swapAreas: NodeListOf<Element> | null) => {
    if (!swapAreas) return false;
    return Array.from(swapAreas).some(area => {
        const rect = area.getBoundingClientRect();
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    });
};

// Swap внутри одной колонки
export const swapInSameColumn = (
    colIdx: number,
    i: number,
    j: number,
    setComponents: TStateSetter<TReturningDraggableComponent[][]>,
) => {
    setComponents(prev => {
        const column = [...prev[colIdx]];
        [column[i], column[j]] = [column[j], column[i]];
        const newState = [...prev];
        newState[colIdx] = column;
        return newState;
    });
};

// Swap между двумя колонками
export const swapBetweenColumns = (
    srcIdx: number,
    dstIdx: number,
    srcIndex: number,
    dstIndex: number,
    setComponents: TStateSetter<TReturningDraggableComponent[][]>,
) => {
    setComponents(prev => {
        const srcCol = [...prev[srcIdx]];
        const dstCol = [...prev[dstIdx]];
        const movedItem = srcCol[srcIndex];
        const targetItem = dstCol[dstIndex];
        srcCol.splice(srcIndex, 1, targetItem);
        dstCol.splice(dstIndex, 1, movedItem);
        const newState = [...prev];
        newState[srcIdx] = srcCol;
        newState[dstIdx] = dstCol;
        return newState;
    });
};

// Обычное перемещение (reorder / move)
export const normalMove = (
    srcIdx: number, dstIdx: number,
    source: {index: number},
    destination: {index: number},
    setComponents: TStateSetter<TReturningDraggableComponent[][]>,
) => {
    if (srcIdx === dstIdx) {
        setComponents(prev => {
            const items = reorder(prev[srcIdx], source.index, destination.index);
            const newState = [...prev];
            newState[srcIdx] = items;
            return newState;
        });
    } else {
        setComponents(prev => {
            const newPosition = move(prev[srcIdx], prev[dstIdx],
                {droppableId: String(srcIdx), index: source.index},
                {droppableId: String(dstIdx), index: destination.index}
            );
            const newState = [...prev];
            newState[srcIdx] = newPosition[srcIdx];
            newState[dstIdx] = newPosition[dstIdx];
            return newState.filter(col => col.length);
        });
    }
};